import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Loader from '@gym-atoms/Loader/Loader';
import Table, { TableProps } from '@gym-molecules/Table/Table';
import Text from '@gym-atoms/Text/Text';
import { deleteAccessCategory, fetchAccessCategories } from '@gym-redux/slices/accessCategorySlice';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { GymChainAccessCategoryTable } from '../../../particles/types/GymChainAccessCategory';
import { GymAccessCategoryType } from '../../../particles/types/AccessCategoryMapping';

type AccessCategoryDetail = GymChainAccessCategoryTable['accessCategoryDetails'][number];

const toAccessCategoryDetail = (accessCategory: GymAccessCategoryType): AccessCategoryDetail => {
  return {
    siteLocationAccessCategoryId: accessCategory.siteLocationAccessCategoryId,
    siteLocationId: accessCategory.siteLocationId,
    siteLocationMembershipId: accessCategory.siteLocationMembershipId,
    accessCategoryId: accessCategory.accessCategoryId,
    locationName: accessCategory.locationName,
    arxAccessCategoryId: accessCategory.arxAccessCategoryId,
    membershipName: accessCategory.membershipName,
    membershipImportedId: accessCategory.importedId,
    program: accessCategory.program,
    scheduleType: accessCategory.scheduleType
  };
};

const isSameLocationAccessCategory = (
  current: AccessCategoryDetail,
  next: AccessCategoryDetail
): boolean => {
  return (
    current.siteLocationId === next.siteLocationId &&
    current.siteLocationAccessCategoryId === next.siteLocationAccessCategoryId
  );
};

const appendUniqueLocationAccessCategory = (
  details: AccessCategoryDetail[],
  detail: AccessCategoryDetail
): AccessCategoryDetail[] => {
  if (details.some((current) => isSameLocationAccessCategory(current, detail))) {
    return details;
  }

  return [...details, detail];
};

const toAccessCategoryRow = (
  accessCategory: GymAccessCategoryType
): GymChainAccessCategoryTable => {
  return {
    id: accessCategory.id,
    siteId: accessCategory.siteId,
    name: accessCategory.name,
    gender: accessCategory.gender,
    age: accessCategory.age > 0 ? accessCategory.age : null,
    schedulesOnly: accessCategory.schedulesOnly,
    status: accessCategory.status,
    accessCategoryDetails: [toAccessCategoryDetail(accessCategory)]
  };
};

const mapAccessCategories = (
  accessCategories: GymAccessCategoryType[] | undefined,
  siteLocationId: number
): GymChainAccessCategoryTable[] | undefined => {
  if (!accessCategories) {
    return undefined;
  }

  return accessCategories
    .filter((accessCategory) => accessCategory.siteLocationId === siteLocationId)
    .reduce<GymChainAccessCategoryTable[]>((mappedAccessCategories, accessCategory) => {
      const detail = toAccessCategoryDetail(accessCategory);
      const mappedIndex = mappedAccessCategories.findIndex(
        (mappedAccessCategory) =>
          mappedAccessCategory.accessCategoryDetails[0]?.accessCategoryId ===
          accessCategory.accessCategoryId
      );

      if (mappedIndex === -1) {
        return [...mappedAccessCategories, toAccessCategoryRow(accessCategory)];
      }

      return mappedAccessCategories.map((mappedAccessCategory, index) => {
        if (index !== mappedIndex) {
          return mappedAccessCategory;
        }

        return {
          ...mappedAccessCategory,
          accessCategoryDetails: appendUniqueLocationAccessCategory(
            mappedAccessCategory.accessCategoryDetails,
            detail
          )
        };
      });
    }, []);
};

const AccessCategoryMappingList = (props: AccessCategoryMappingListProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<GymChainAccessCategoryTable>();
  const [selectedRows, setSelectedRows] = useState<Array<GymChainAccessCategoryTable>>([]);
  const [accessCategorySortField, setAccessCategorySortField] = useState('id');
  const [accessCategorySortOrder, setAccessCategorySortOrder] = useState(-1);
  const [accessCategoryOffset, setAccessCategoryOffset] = useState(0);
  const [accessCategoryPageSize, setAccessCategoryPageSize] = useState(10);
  const isAccessCategoryLoading = useAppSelector(
    (state) => state.accessCategory.isAccessCategoriesLoading
  );
  const [accessCategorySearchTerm, setAccessCategorySearchTerm] = useState('');
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const userRole = useAppSelector((state) => state.user.userRole);
  const totalAccessCategoryCount =
    useAppSelector(
      (state) =>
        state.accessCategory.items.find((g) => g.siteLocationId === props.gymId)?.totalRecords
    ) || 0;

  useEffect(() => {
    dispatch(
      fetchAccessCategories({
        siteLocationId: props.gymId
      })
    );
  }, [props.gymId]);

  useEffect(() => {
    console.log({ selectedRows });
  }, [selectedRows]);

  console.log(props);
  const gymAccessCategories = useAppSelector((state) =>
    mapAccessCategories(
      state.accessCategory.items.find((e) => e.siteLocationId === props.gymId)?.items,
      props.gymId
    )
  );

  const menuItem = [
    {
      label: t('COMMON.MODIFY'),
      command: () =>
        history.push({
          pathname: `/gymChains/${props.chainId}/gyms/${props.gymId}/gymAccessCategories/${selectedRow?.id}`,
          state: { from: 'gym' }
        })
    },
    {
      label: t('COMMON.DELETE'),
      command: () => {
        setSelectedRows(selectedRow ? [selectedRow] : []);
        setIsDeleteDialogVisible(true);
      }
    }
  ];

  // const checkAccessCategoryDeletable = async () => {
  //   setIsDeleteDialogVisible(true);
  //   setAccessCategoryDeletableCheckLoading(true);
  //   console.log(selectedRows);
  //   try {
  //     const response = await (API.graphql({
  //       query: isAccessCategoryLinked,
  //       variables: {
  //         locationAccessCategoryId: selectedRows
  //       }
  //     }) as Promise<{
  //       data: { isAccessCategoryLinked: CountResponse };
  //     }>);
  //     if (
  //       response.data.isAccessCategoryLinked.count !== undefined &&
  //       response.data.isAccessCategoryLinked.count !== null
  //     ) {
  //       response.data.isAccessCategoryLinked.count > 0
  //         ? setAccessCategoryDeletableStatus(false)
  //         : setAccessCategoryDeletableStatus(true);
  //     } else {
  //       console.log('Error Delete Validation Query');
  //       setErrorDialogVisible(true);
  //     }
  //     setAccessCategoryDeletableCheckLoading(false);
  //   } catch (error) {
  //     setErrorDialogVisible(true);
  //     setIsDeleteDialogVisible(false);
  //     console.log('Catch: ', error);
  //     setAccessCategoryDeletableStatus(false);
  //     setAccessCategoryDeletableCheckLoading(false);
  //   }
  // };

  const tablePropsAccessCategory: TableProps<GymChainAccessCategoryTable> = {
    exportFileName: t('ACCESS_CATEGORY.EXPORT_EXCEL_FILE_NAME'),
    selectionMode: 'checkbox',
    setPageSize: setAccessCategoryPageSize,
    setOffset: setAccessCategoryOffset,
    pageSize: accessCategoryPageSize,
    offset: accessCategoryOffset,
    setSortField: setAccessCategorySortField,
    setSortOrder: setAccessCategorySortOrder,
    sortField: accessCategorySortField,
    sortOrder: accessCategorySortOrder,
    totalRecords: totalAccessCategoryCount,
    uniqueFieldName: 'id',
    data: gymAccessCategories || undefined,
    searchTerm: accessCategorySearchTerm,
    setSearchTerm: setAccessCategorySearchTerm,
    emptyStateTexts: [t('ACCESS_CATEGORY.EMPTY_STATE_TEXT')],
    columns: [
      {
        field: 'name',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_NAME'),
        toggable: false,
        sortable: true,
        truncateText: true
      },
      {
        field: 'gender',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_GENDER'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'age',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_AGE'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'schedulesOnly',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_SCHEDULES_ONLY'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'status',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_STATUS'),
        toggable: true,
        truncateText: true
      }
    ],
    isContextMenu: true,
    menuItem: menuItem,
    lazy: false,
    expandable: true,
    expandableField: 'accessCategoryDetails',
    expandedCols: [
      {
        field: 'locationName',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_LOCATION_NAME'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'arxAccessCategoryId',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_ARX_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'membershipImportedId',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_MEMBERSHIP_IMPORTED_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'siteLocationMembershipId',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_LOCATION_MEMBERSHIP_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'membershipName',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_MEMBERSHIP_NAME'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'program',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'scheduleType',
        header: t('GYM_ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_SCHEDULE_TYPE'),
        toggable: true,
        truncateText: true
      }
    ],
    headerBtns: [
      <div className="d-flex" key={1}>
        <Button
          label={t('GYM_ACCESS_CATEGORY.ADD_NEW_ACCESS_CATEGORY_BUTTON_LABEL')}
          size={ButtonSizes.small}
          onClick={() =>
            history.push(`/gymChains/${props.chainId}/gyms/${props.gymId}/gymAccessCategories/new`)
          }
        />
      </div>,
      <div className="d-flex" key={2}>
        <Button
          data-cy={'access-categoryTable -table-btn-inactive-beacons'}
          variant={ButtonVariants.outline}
          label={t('ACCESS_CATEGORY.DELETE_SELECTED_ACCESS_CATEGORY_BUTTON_LABEL')}
          size={ButtonSizes.small}
          onClick={() => {
            setIsDeleteDialogVisible(true);
          }}
          disabled={selectedRows?.length === 0 || false}
        />
      </div>
    ],
    searchPlaceholderText: t('ACCESS_CATEGORY.SEARCH_FIELD_TEXT'),
    excelBtntext: t('ACCESS_CATEGORY.EXPORT_EXCEL_BUTTON_LABEL'),
    selectedRows: selectedRows,
    selectedRow: selectedRow,
    setSelectedRow: setSelectedRow,
    setSelectedRows: setSelectedRows
  };

  const deleteDialogHandler = async () => {
    setIsDeleting(true);
    console.log({ selectedRows });
    const locationAccessCategoryIds = selectedRows
      .flatMap((row) =>
        row.accessCategoryDetails.map((detail) => detail.siteLocationAccessCategoryId)
      )
      .filter((id) => Number.isFinite(id));

    if (locationAccessCategoryIds.length > 0) {
      await dispatch(
        deleteAccessCategory({
          ids: locationAccessCategoryIds,
          siteLocationId: props.gymId
        })
      );
      dispatch(fetchAccessCategories({ siteLocationId: props.gymId }));
    }

    setIsDeleting(false);
    setIsDeleteDialogVisible(false);
  };

  const DeleteDialogFooter = () => {
    return (
      <div>
        <>
          <Button
            label={t('COMMON.CANCEL')}
            variant={ButtonVariants.textonly}
            onClick={() => setIsDeleteDialogVisible(false)}
          />
          <Button
            label={t('COMMON.DELETE')}
            variant={ButtonVariants.danger}
            onClick={deleteDialogHandler}
            icon={isDeleting ? 'pi-spinner pi-spin' : ''}
          />
        </>
      </div>
    );
  };

  const ErrorDialogFooter = () => {
    return (
      <div>
        <Button
          label={t('COMMON.OK')}
          variant={ButtonVariants.textonly}
          onClick={() => setErrorDialogVisible(false)}
        />
      </div>
    );
  };

  return (
    <>
      <DialogBox
        dialogVisible={isDeleteDialogVisible}
        variant={DialogBoxVariants.long}
        dialogDismissableMask={true}
        onHideCallback={() => setIsDeleteDialogVisible(false)}
        dialogFooter={<DeleteDialogFooter />}
        dialogClosable={false}
        dialogHeader={t('COMMON.DELETE')}
        content={
          <Text>
            {t('ACCESS_CATEGORY.DELETE_MODAL_CONFIRM')}{' '}
            <span className="fw-bold">{selectedRows?.map((row) => row.name).join(', ')}</span>?
          </Text>
        }
      />
      <DialogBox
        dialogVisible={errorDialogVisible}
        variant={DialogBoxVariants.long}
        dialogDismissableMask={true}
        onHideCallback={() => setErrorDialogVisible(false)}
        dialogFooter={<ErrorDialogFooter />}
        dialogClosable={false}
        dialogHeader={t('ACCESS_CATEGORY.ERROR_HEADER')}
        content={<Text>{t('ACCESS_CATEGORY.ERROR_MESSAGE')}</Text>}
      />
      {isAccessCategoryLoading ? <Loader shape="table" /> : <Table {...tablePropsAccessCategory} />}
    </>
  );
};

export interface AccessCategoryMappingListProps {
  gymId: number;
  chainId: number;
  heading?: string;
}

export default AccessCategoryMappingList;
